import { createIcon } from './icon.js';

export interface NewsCardData {
  id: number;
  title: string;
  body: string;
  isPinned: boolean;
  isPublished: boolean;
  publishedAt: string;
  authorName: string;
  tags?: string[];
  images: Array<{
    uuid: string;
    originalName: string | null;
    mimeType: string;
    width: number | null;
    height: number | null;
  }>;
  documents?: Array<{
    uuid: string;
    originalName: string;
    mimeType: string;
    sizeBytes: number;
  }>;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
}

function truncate(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen).replace(/\s+\S*$/, '') + '…';
}

function extractFirstImageUuid(news: NewsCardData): string | null {
  if (news.images.length > 0) return news.images[0]!.uuid;
  const m = /!\[[^\]]*\]\(([^)]+)\)/.exec(news.body);
  return m ? (m[1] ?? null) : null;
}

function buildHeroCard(news: NewsCardData, thumbUuid: string, onNavigate: (href: string) => void): HTMLElement {
  const card = document.createElement('article');
  card.className = `news-card news-card--hero${news.isPinned ? ' news-card--pinned' : ''}`;

  // Hero image
  const picture = document.createElement('picture');
  picture.className = 'news-card__hero-image';

  const avif = document.createElement('source');
  avif.srcset = `/uploads/news/${thumbUuid}/medium.avif`;
  avif.type = 'image/avif';
  picture.appendChild(avif);

  const webp = document.createElement('source');
  webp.srcset = `/uploads/news/${thumbUuid}/medium.webp`;
  webp.type = 'image/webp';
  picture.appendChild(webp);

  const img = document.createElement('img');
  img.src = `/uploads/news/${thumbUuid}/medium.jpg`;
  img.alt = '';
  img.loading = 'lazy';
  picture.appendChild(img);

  card.appendChild(picture);

  // Body
  const body = document.createElement('div');
  body.className = 'news-card__body';

  if (news.isPinned) {
    const badge = document.createElement('div');
    badge.className = 'news-card__badge';
    badge.appendChild(createIcon('pin', 12));
    const span = document.createElement('span');
    span.textContent = 'Закреплено';
    badge.appendChild(span);
    body.appendChild(badge);
  }

  const title = document.createElement('h2');
  title.className = 'news-card__title';
  title.textContent = news.title;
  body.appendChild(title);

  const cleanBody = news.body.replace(/!\[[^\]]*\]\([^)]*\)/g, '');
  const excerpt = document.createElement('p');
  excerpt.className = 'news-card__excerpt';
  excerpt.textContent = truncate(cleanBody, 160);
  body.appendChild(excerpt);

  appendTagsAndFooter(body, news);
  card.appendChild(body);

  setupCardInteraction(card, news, onNavigate);
  return card;
}

function buildCompactCard(news: NewsCardData, onNavigate: (href: string) => void): HTMLElement {
  const card = document.createElement('article');
  card.className = `news-card${news.isPinned ? ' news-card--pinned' : ''}`;

  const body = document.createElement('div');
  body.className = 'news-card__body';

  if (news.isPinned) {
    const badge = document.createElement('div');
    badge.className = 'news-card__badge';
    badge.appendChild(createIcon('pin', 12));
    const span = document.createElement('span');
    span.textContent = 'Закреплено';
    badge.appendChild(span);
    body.appendChild(badge);
  }

  const title = document.createElement('h2');
  title.className = 'news-card__title';
  title.textContent = news.title;
  body.appendChild(title);

  const cleanBody = news.body.replace(/!\[[^\]]*\]\([^)]*\)/g, '');
  const excerpt = document.createElement('p');
  excerpt.className = 'news-card__excerpt';
  excerpt.textContent = truncate(cleanBody, 200);
  body.appendChild(excerpt);

  appendTagsAndFooter(body, news);
  card.appendChild(body);

  setupCardInteraction(card, news, onNavigate);
  return card;
}

function appendTagsAndFooter(body: HTMLElement, news: NewsCardData): void {
  if (news.tags && news.tags.length > 0) {
    const tagsEl = document.createElement('div');
    tagsEl.className = 'news-card__tags';
    for (const tag of news.tags) {
      const chip = document.createElement('span');
      chip.className = 'news-tag-chip';
      chip.textContent = tag;
      tagsEl.appendChild(chip);
    }
    body.appendChild(tagsEl);
  }

  if (news.documents && news.documents.length > 0) {
    const docsHint = document.createElement('div');
    docsHint.className = 'news-card__docs-hint';
    const icon = createIcon('paperclip', 12);
    docsHint.appendChild(icon);
    const span = document.createElement('span');
    span.textContent = `${news.documents.length} вложени${news.documents.length === 1 ? 'е' : news.documents.length < 5 ? 'я' : 'й'}`;
    docsHint.appendChild(span);
    body.appendChild(docsHint);
  }

  const footer = document.createElement('div');
  footer.className = 'news-card__footer';

  const date = document.createElement('time');
  date.className = 'news-card__date';
  date.dateTime = news.publishedAt;
  date.textContent = formatDate(news.publishedAt);
  footer.appendChild(date);

  body.appendChild(footer);
}

function setupCardInteraction(card: HTMLElement, news: NewsCardData, onNavigate: (href: string) => void): void {
  card.addEventListener('click', () => onNavigate(`/news/${news.id}`));
  card.setAttribute('tabindex', '0');
  card.setAttribute('role', 'button');
  card.setAttribute('aria-label', `Читать: ${news.title}`);
  card.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') onNavigate(`/news/${news.id}`);
  });
}

export function createNewsCard(news: NewsCardData, onNavigate: (href: string) => void): HTMLElement {
  const thumbUuid = extractFirstImageUuid(news);
  if (thumbUuid) {
    return buildHeroCard(news, thumbUuid, onNavigate);
  }
  return buildCompactCard(news, onNavigate);
}
